import React from 'react'
import { AppService } from '../../../services'
import { useService } from '@pushly/aqe/lib/hooks'
import { Flex, Skeleton, Space } from 'antd'
import moment from 'moment-timezone'
import { SegmentModel } from '../../../models/segments/segment.model'
import { numberWithCommas } from '../../../_utils/utils'
import { Button } from '@pushly/aqe/lib/components'
import { asCaslSubject, CurrentUserCan, getDemoAttributesFor } from '../../../stores/app-ability'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { SubjectEntity } from '../../../enums/ability-entity.enum'
import { determineChannelBadge } from '../../../components/badges/_utils'
import { FEAT_CHANNEL_ANDROID, FEAT_CHANNEL_IOS } from '../../../constants'
import { hasFlag } from '../../../core/flags/has-flag'
import { AppState } from '../../../stores/app'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { SegmentType } from '@pushly/models/lib/enums/segment-type'
import { DetailsViewPageHeader } from '../../../components/details-view/details-view-page-header'
import { generateShortID } from '../../../_utils/uid'

interface IHeaderProps {
    segment: SegmentModel
}

export const SegmentDetailsHeader = ({ segment }: IHeaderProps): JSX.Element => {
    const appService = useService(AppService)
    const appState = useService(AppState)

    const editSegmentUrl = () => {
        return appService.routeWithin('domain', `/segments/${segment.getId()}`, true)
    }

    const goToEditSegment = () => {
        const editRoute = editSegmentUrl()
        return appService.route(editRoute)
    }

    const domain = appState.currentDomain!
    const domainHasNativeIOS = hasFlag(domain, FEAT_CHANNEL_IOS)
    const domainHasNativeAndroid = hasFlag(domain, FEAT_CHANNEL_ANDROID)

    const viewHasNativePush =
        (domainHasNativeIOS && domain.nativeApnsConfiguration?.isActive) ||
        (domainHasNativeAndroid && domain.nativeFcmConfiguration?.isActive)

    const segmentTitle = segment?.getName() === 'All' ? 'All Subscribers' : segment?.getName() ?? ''

    const buildSubOptions = (): React.ReactNode => {
        if (!segment) return null

        const channelBadges = segment
            .getChannels()
            .filter((val) => (!viewHasNativePush ? val === DeliveryChannel.WEB : true))
            .map((ch) => (
                <span key={ch} className="option">
                    {determineChannelBadge(ch)}
                </span>
            ))

        if (channelBadges.length > 0) {
            channelBadges.unshift(<span key={generateShortID()}>|</span>)
        }

        return <Space>{channelBadges}</Space>
    }

    return (
        <div className="details-header">
            <Skeleton loading={!segment} active={true}>
                {segment && (
                    <div className="segment-title">
                        <DetailsViewPageHeader
                            title={segmentTitle}
                            resource={{
                                id: segment.getId(),
                                createdByUserName: segment.getCreatedByUserName(),
                            }}
                            action={
                                segment.getType() === SegmentType.STANDARD &&
                                !segment.getIsDefault() && (
                                    <CurrentUserCan
                                        do={AbilityAction.UPDATE}
                                        on={asCaslSubject(SubjectEntity.SEGMENT, {
                                            ...segment,
                                            ...getDemoAttributesFor(segment.getDomainId(), appState.currentDomain),
                                        })}
                                    >
                                        <Button
                                            type="primary"
                                            onClick={() => goToEditSegment()}
                                            altHref={editSegmentUrl()}
                                        >
                                            <span>Edit Segment</span>
                                        </Button>
                                    </CurrentUserCan>
                                )
                            }
                            buildSubOptions={buildSubOptions}
                            addOnAfter={() => (
                                <Flex vertical gap={4}>
                                    <div>
                                        <span>
                                            <b>Segment Size</b> {numberWithCommas(segment.getEstimatedReach())}
                                        </span>
                                    </div>
                                    <div>
                                        <span>
                                            <b>Last Targeted</b>{' '}
                                            {segment.getLastTargetedDateUtc()
                                                ? moment(segment.getLastTargetedDateUtc()).format('dddd MMMM D, YYYY')
                                                : 'Not Yet Targeted'}
                                        </span>
                                    </div>
                                </Flex>
                            )}
                        />
                    </div>
                )}
            </Skeleton>
        </div>
    )
}
